import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { i18nTitle: { type: String, default: "Confirm" } };
  static targets = ["title", "message", "confirmBtn"];

  #resolve = null;
  #defaultConfirmLabel = "";
  #defaultConfirmClass = "";

  connect() {
    this.#defaultConfirmLabel = this.confirmBtnTarget.textContent.trim();
    this.#defaultConfirmClass = this.confirmBtnTarget.dataset.confirmDialogDefaultClass || this.confirmBtnTarget.className;
    // Override Turbo's confirm so data-turbo-confirm uses this dialog.
    // The submitter's data-turbo-confirm-button sets the confirm label.
    // Turbo-driven confirms are all destructive/overwrite-style actions
    // today, so they keep the danger-red button unless told otherwise.
    Turbo.config.forms.confirm = (message, _element, submitter) =>
      this.#ask(message, {
        confirmLabel: submitter?.dataset?.turboConfirmButton,
        confirmClass: submitter?.dataset?.turboConfirmClass,
      });
  }

  disconnect() {
    Turbo.config.forms.confirm = undefined;
  }

  // Opened via `confirm-dialog:open@window`, e.g.
  //   dispatchEvent(new CustomEvent("confirm-dialog:open", {
  //     detail: { title, message, confirmLabel, confirmClass, onConfirm: () => {...} }
  //   }))
  // confirmClass lets the caller pick the confirm button's tone — omit it
  // for the default danger-red (destructive actions), or pass e.g.
  // "btn btn--primary" for an affirmative, non-destructive confirm (like
  // saving a draft) so the button doesn't look like it's about to delete
  // something it isn't.
  // messageHtml is only ever caller-supplied (never end-user input) — e.g.
  // bgm-wizard building an old→new track diff — so innerHTML is safe here.
  open(event) {
    const { title, message, messageHtml, confirmLabel, confirmClass, onConfirm } = event.detail || {};
    this.#ask(message, { title, messageHtml, confirmLabel, confirmClass }).then((ok) => {
      if (ok) onConfirm?.();
    });
  }

  // Opens the dialog and returns a Promise resolved with true/false.
  #ask(message, { title, messageHtml, confirmLabel, confirmClass } = {}) {
    this.titleTarget.textContent = title || this.i18nTitleValue;
    if (messageHtml) {
      this.messageTarget.innerHTML = messageHtml;
    } else {
      this.messageTarget.textContent = message || "";
    }
    this.confirmBtnTarget.textContent = confirmLabel || this.#defaultConfirmLabel;
    this.confirmBtnTarget.className = confirmClass || this.#defaultConfirmClass;
    this.element.hidden = false;
    // [hidden] is display:none, which can't transition — remove it, then add
    // the shape-open class next frame so the panel scale/opacity actually animates.
    requestAnimationFrame(() => this.element.classList.add("confirm-dialog--open"));
    this.confirmBtnTarget.focus();
    document.body.style.overflow = "hidden";

    return new Promise((resolve) => {
      this.#resolve = resolve;
    });
  }

  confirm() {
    this.#close(true);
  }

  cancel() {
    this.#close(false);
  }

  keydown(e) {
    if (this.element.hidden) return;
    if (e.key === "Escape") this.#close(false);
    if (e.key === "Enter") this.#close(true);
  }

  #close(result) {
    this.element.classList.remove("confirm-dialog--open");
    document.body.style.overflow = "";
    this.#resolve?.(result);
    this.#resolve = null;
    // Wait for the close transition before hiding, so the panel shrinks away
    // instead of vanishing mid-animation. Falls back to a timer if the panel
    // has no transition (prefers-reduced-motion).
    const panel = this.element.querySelector(".confirm-dialog__panel");
    const duration = panel ? parseFloat(getComputedStyle(panel).transitionDuration) * 1000 : 0;
    if (duration > 0) {
      panel.addEventListener("transitionend", () => { this.element.hidden = true; }, { once: true });
    } else {
      this.element.hidden = true;
    }
  }
}
